import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast, Toaster } from "sonner";
import { ScannerBar } from "@/components/ScannerBar";
import { SignalsFeed } from "@/components/SignalsFeed";
import { TriggerHistory } from "@/components/TriggerHistory";
import { WinnerProfilePanel } from "@/components/WinnerProfilePanel";
import { AutoDiscoveredIndicators } from "@/components/AutoDiscoveredIndicators";
import { CryptometerPanel } from "@/components/CryptometerPanel";
import { FVGTable } from "@/components/FVGTable";
import { BacktestPanel } from "@/components/BacktestPanel";
import { PairDetail } from "@/components/PairDetail";
import { SignalHistory } from "@/components/SignalHistory";
import { WatchlistPanel } from "@/components/WatchlistPanel";
import { LearningPanel } from "@/components/LearningPanel";
import { AboutModal } from "@/components/AboutModal";
import { AlertsControlPanel } from "@/components/AlertsControlPanel";
import { FilterPresets } from "@/components/FilterPresets";
import { UniverseCoverage } from "@/components/UniverseCoverage";
import { LiveHeatmap } from "@/components/LiveHeatmap";
import { api, fmtNum } from "@/api";
import { CurrencyBtc, TrendUp, TrendDown, SpeakerHigh, SpeakerSlash, Info, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAlertSound } from "@/hooks/useAlertSound";

export default function Dashboard() {
  const qc = useQueryClient();
  const [minScore, setMinScore] = useState(0);
  const [dirFilter, setDirFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [selectedPair, setSelectedPair] = useState(null);
  const [selectedDir, setSelectedDir] = useState("long");
  const [selectedMode, setSelectedMode] = useState("scalp");

  const status = useQuery({ queryKey: ["status"], queryFn: api.status, refetchInterval: 5000 });
  const pairs = useQuery({ queryKey: ["pairs"], queryFn: () => api.pairs(), refetchInterval: 60_000, staleTime: 60_000 });
  const signals = useQuery({
    queryKey: ["signals", minScore, dirFilter, modeFilter],
    queryFn: () => api.liveSignals(minScore, dirFilter, modeFilter),
    refetchInterval: 10_000,
  });

  const refresh = useMutation({
    mutationFn: api.refreshScanner,
    onSuccess: (d) => {
      toast.success(`Refreshed universe · ${d.count} pairs tracked`);
      qc.invalidateQueries({ queryKey: ["status"] });
      qc.invalidateQueries({ queryKey: ["pairs"] });
    },
    onError: (err) => {
      // Surface the specific server error so the user can distinguish between OKX
      // rate limiting, timeouts, DNS failures, etc.
      const detail = err?.response?.data?.detail || err?.message || "network error";
      toast.error(`Universe refresh failed — ${String(detail).slice(0, 160)}`);
    },
  });

  const topMovers = useMemo(() => {
    if (!pairs.data?.pairs) return [];
    // Sort by ABSOLUTE change so we surface both big gainers and big losers.
    return [...pairs.data.pairs]
      .sort((a, b) => Math.abs(b.change_24h_pct) - Math.abs(a.change_24h_pct))
      .slice(0, 24);
  }, [pairs.data]);

  const openPair = (symbol, dir = "long", mode = "scalp") => {
    setSelectedPair(symbol);
    setSelectedDir(dir);
    setSelectedMode(mode);
  };

  const sound = useAlertSound(signals.data?.signals);

  return (
    <div className="min-h-screen text-white p-3 md:p-6" data-testid="dashboard-root">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1700px] mx-auto flex flex-col gap-4">
        <Header sound={sound} />

        <ScannerBar
          status={status.data}
          onRefresh={() => refresh.mutate()}
          isRefreshing={refresh.isPending}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 border border-white/10 bg-[#0A0A0A] grid-lines">
            <div className="px-4 py-3 border-b border-white/10 bg-[#0f0f0f] flex items-center gap-2">
              <CurrencyBtc size={16} className="text-[#F5C518]" />
              <h2 className="font-display font-bold text-sm tracking-widest uppercase text-white">Top Movers 24h · WEEX / BLOFIN Tradeable</h2>
              <span className="ml-auto font-mono text-[11px] text-white/40" data-testid="pair-universe-count">
                {pairs.data?.count || 0} pairs tracked · sorted by biggest 24h move
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 max-h-[280px] overflow-y-auto" data-testid="top-movers-grid">
              {topMovers.map((p) => {
                const up = p.change_24h_pct >= 0;
                return (
                  <button
                    key={p.symbol}
                    data-testid={`mover-${p.symbol}`}
                    onClick={() => openPair(p.symbol, "long", "scalp")}
                    className="text-left border-r border-b border-white/5 last:border-r-0 hover:bg-white/[0.03] px-3 py-3 transition"
                  >
                    <div className="flex items-center gap-1">
                      <span className="font-display font-bold text-sm text-white">{p.base}</span>
                      {up ? <TrendUp size={12} className="text-[#00FF66]" /> : <TrendDown size={12} className="text-[#FF3B30]" />}
                    </div>
                    <div className="font-mono text-[12px] mt-1 text-white/80">{fmtNum(p.price)}</div>
                    <div className={`font-mono text-[11px] mt-0.5 ${up ? "text-[#00FF66]" : "text-[#FF3B30]"}`}>
                      {up ? "+" : ""}{p.change_24h_pct.toFixed(2)}%
                    </div>
                  </button>
                );
              })}
              {topMovers.length === 0 && (
                <div className="col-span-full p-6 text-center text-white/40 font-mono text-xs">
                  Loading market<span className="terminal-cycle">...</span>
                </div>
              )}
            </div>
          </div>
          <div className="border border-white/10 bg-[#0A0A0A] p-4 flex flex-col gap-2">
            <div className="font-display font-bold text-xs tracking-widest uppercase text-white/60">Highest Score Now</div>
            {signals.data?.signals?.[0] ? (
              <button
                data-testid="top-signal-card"
                onClick={() =>
                  openPair(
                    signals.data.signals[0].symbol,
                    signals.data.signals[0].direction || "long",
                    signals.data.signals[0].mode || "scalp",
                  )
                }
                className="flex-1 text-left"
              >
                <div
                  className="font-display font-bold text-2xl"
                  style={{ color: signals.data.signals[0].direction === "short" ? "#FF3B30" : "#00FF66" }}
                >
                  {signals.data.signals[0].symbol}
                </div>
                <div className="font-mono text-[11px] text-white/60 mt-1 uppercase tracking-widest">
                  {signals.data.signals[0].direction || "long"} · {signals.data.signals[0].mode || "scalp"} · Score {signals.data.signals[0].score} · Lev {signals.data.signals[0].recommended_leverage}×
                </div>
                <div className="font-mono text-[11px] text-white/60">Entry {fmtNum(signals.data.signals[0].entry)}</div>
                <div
                  className="mt-3 text-[10px] font-mono uppercase tracking-widest"
                  style={{ color: signals.data.signals[0].direction === "short" ? "#FF3B30" : "#00FF66" }}
                >
                  → Open analysis
                </div>
              </button>
            ) : (
              <div className="flex-1 flex items-center text-white/40 font-mono text-xs">
                No qualifying setup right now. Engine is watching.
              </div>
            )}
          </div>
        </div>

        {/* Watchlist + Signals row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 h-[720px]">
            <SignalsFeed
              signals={signals.data?.signals}
              isLoading={signals.isLoading}
              onSelect={(s) => openPair(s.symbol, s.direction || "long", s.mode || "scalp")}
              filter={minScore}
              setFilter={setMinScore}
              dirFilter={dirFilter}
              setDirFilter={setDirFilter}
              modeFilter={modeFilter}
              setModeFilter={setModeFilter}
            />
          </div>
          <div className="lg:col-span-1 flex flex-col gap-4">
            <WatchlistPanel pairs={pairs.data?.pairs || []} onSelect={(s) => openPair(s, "long", "scalp")} />
            <div className="flex-1">
              <FVGTable pairs={pairs.data?.pairs || []} />
            </div>
          </div>
          <div className="lg:col-span-1 h-[720px]">
            <SignalHistory onSelect={(sym) => openPair(sym, "long", "scalp")} />
          </div>
        </div>

        {/* Trigger history — enterability tracker */}
        <TriggerHistory
          onSelect={(sym, dir, mode) => openPair(sym, dir || "long", mode || "scalp")}
        />

        {/* Winner Profile — data-driven criterion analysis */}
        <WinnerProfilePanel />
        {/* Self-Evolving Indicators — bot mines its own history for new winning patterns */}
        <AutoDiscoveredIndicators />
        {/* Cryptometer.io alpha — external market intelligence */}
        <CryptometerPanel />
        {/* Filter presets */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-3">
            <FilterPresets />
          </div>
        </div>

        {/* Universe coverage widget */}
        <UniverseCoverage onSelect={(sym) => openPair(sym, "long", "swing")} />

        {/* Live Heatmap: colored grid across the entire tracked universe */}
        <LiveHeatmap onSelect={(sym, dir, mode) => openPair(sym, dir || "long", mode || "swing")} />

        {/* Self-learning engine */}
        <div className="h-[560px]">
          <LearningPanel />
        </div>

        {/* Backtest full width */}
        <div className="h-[760px]">
          <BacktestPanel defaultSymbol="BTCUSDT" pairs={pairs.data?.pairs || []} />
        </div>

        <footer className="text-[11px] font-mono text-white/30 py-4 border-t border-white/5 flex items-center justify-between flex-wrap gap-2">
          <span>Data · OKX USDT-Perpetual SWAP public API · symbols cross-referenced against WEEX &amp; BLOFIN.</span>
          <span>
            Not financial advice. High leverage (20-100×) trading is extremely risky. Backtested edges do not guarantee future results.
          </span>
        </footer>
      </div>

      {selectedPair && (
        <PairDetail
          symbol={selectedPair}
          direction={selectedDir}
          mode={selectedMode}
          onClose={() => setSelectedPair(null)}
        />
      )}
    </div>
  );
}

function Header({ sound }) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  return (
    <header className="flex items-center justify-between flex-wrap gap-3" data-testid="dashboard-header">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 border border-[#00FF66]/40 bg-[#00FF66]/10 flex items-center justify-center">
          <div className="w-2 h-2 bg-[#00FF66] pulse-dot" />
        </div>
        <div>
          <h1 className="font-display font-black text-xl md:text-2xl tracking-tight text-white">
            LONGBEAM<span className="text-[#00FF66]">.</span>
          </h1>
          <p className="font-mono text-[11px] text-white/40 uppercase tracking-widest">
            Multi-mode confluence engine · Scalp · Swing · Position
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 font-mono text-[10px] text-white/40 uppercase tracking-widest flex-wrap">
        <button
          data-testid="open-alerts-btn"
          onClick={() => setAlertsOpen(true)}
          className="px-2 py-1 border border-white/10 hover:border-[#00FF66]/60 hover:text-[#00FF66] flex items-center gap-1 transition-colors"
          title="Telegram + news blackout settings"
        >
          <PaperPlaneTilt size={11} weight="fill" />
          Alerts &amp; News
        </button>
        <button
          data-testid="open-about-btn"
          onClick={() => setAboutOpen(true)}
          className="px-2 py-1 border border-white/10 hover:border-[#F5C518]/60 hover:text-[#F5C518] flex items-center gap-1 transition-colors"
          title="Metrics guide & how to use the bot"
        >
          <Info size={11} weight="fill" />
          About
        </button>
        <span className="px-2 py-1 border border-white/10">3-mode scanner</span>
        <span className="px-2 py-1 border border-white/10">HTF trend-flip</span>
        <span className="px-2 py-1 border border-white/10">Signal replay</span>
        <button
          data-testid="alert-sound-toggle"
          onClick={() => sound.setEnabled(!sound.enabled)}
          className={`px-2 py-1 border flex items-center gap-1 ${
            sound.enabled
              ? "border-[#00FF66]/60 text-[#00FF66] bg-[#00FF66]/10"
              : "border-white/10 text-white/50"
          }`}
          title={sound.enabled ? `Sound on · chime at score ≥ ${sound.threshold}` : "Sound off"}
        >
          {sound.enabled ? <SpeakerHigh size={11} weight="fill" /> : <SpeakerSlash size={11} />}
          {sound.enabled ? `Alert ≥${sound.threshold}` : "Alert Off"}
        </button>
      </div>
      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <AlertsControlPanel open={alertsOpen} onClose={() => setAlertsOpen(false)} />
    </header>
  );
}
